import { debounce } from "lodash";
import { useCallback, useEffect, useRef } from "react";

type Props = {
  follow: boolean;
  onCancel: () => void;
  scrollContainer: HTMLElement | Document | null;
  contentElement: HTMLElement | null;
};

/**
 * useScrollFollower automatically scrolls a container to the bottom when the inner content changes size.
 *
 * If a manual scroll is detected, the automatic scrolling is canceled.
 */
export const useScrollFollow = ({ follow, onCancel, contentElement, scrollContainer }: Props) => {
  // Flag to indicate if automatic scrolling is in progress so that we know when to cancel if manually scrolling
  const isAutoScrolling = useRef(false);

  // Keep a synchronous ref of the follow state so ResizeObserver callbacks
  // that fire between a state update and the effect cleanup don't scroll.
  const followRef = useRef(follow);
  followRef.current = follow;

  /**
   * Debounced callback for resetting the auto scroll flag.
   *
   * The small delay prevents automatic scrolling from being canceled prematurely and debouncing (rather than setTimeout)
   * handles quick in succession content updates.
   */
  const finishAutoScroll = useCallback(
    debounce(() => {
      isAutoScrolling.current = false;
    }, 100),
    [],
  );

  const preventScrollCancellation = useCallback(() => {
    isAutoScrolling.current = true;
    finishAutoScroll();
  }, [finishAutoScroll]);

  const scrollToEnd = useCallback(() => {
    if (!followRef.current) {
      return;
    }

    preventScrollCancellation();

    if (Features.BuildSidebar) {
      // New UI works better by scrolling to the end of the scroll container. Ignoring things like scroll margin offsets etc.
      if (scrollContainer) {
        (scrollContainer as HTMLElement).scrollTop = (scrollContainer as HTMLElement).scrollHeight;
      }
    } else {
      // Classic UI uses scrollIntoView since we're scrolling the entire window.
      contentElement?.scrollIntoView({ block: "end" });
    }
  }, [scrollContainer, contentElement, preventScrollCancellation]);

  // If a manual scroll is detected, cancel automatic scrolling
  const cancelScroll = useCallback(() => {
    if (!isAutoScrolling.current) {
      onCancel();
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      scrollToEnd();
    });

    if (follow) {
      if (contentElement && scrollContainer) {
        scrollToEnd();
        scrollContainer?.addEventListener("scroll", cancelScroll);
        contentElement.addEventListener("click", preventScrollCancellation);
        resizeObserver.observe(contentElement);
      }
    }

    return () => {
      scrollContainer?.removeEventListener("scroll", cancelScroll);
      contentElement?.removeEventListener("click", preventScrollCancellation);
      resizeObserver.disconnect();
      finishAutoScroll.cancel();
    };
  }, [
    follow,
    scrollContainer,
    contentElement,
    scrollToEnd,
    cancelScroll,
    preventScrollCancellation,
    finishAutoScroll,
  ]);
};
